import parse from 'html-react-parser'
import { DateTime } from 'luxon'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { NumberParam, useQueryParams as useParamsFromQuery } from 'use-query-params'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomScrollbar from '@components/CustomScrollbar'
import { EllipsisMenu, EllipsisMenuItem } from '@components/EllipsisMenu'
import ErrorPopup, { ErrorPopupProps } from '@components/ErrorPopup'
import NoticePopover from '@components/NoticePopover'
import { isArray } from '@craco/craco/dist/lib/utils'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import alertsApi from '@modules/alerts/api'
import ProfileAlertPopover from '@modules/alerts/components/ProfileAlertPopover'
import { AlertFrequencyType, ProfileAlert } from '@modules/alerts/types'
import { P1PlusAccessConfigEvent } from '@modules/auth/hooks/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import { prepareMoveOrCopyEventData, preparePendoCustomColumnEvent, preparePendoCustomListEvent } from '@modules/pendo'
import { usePendoTrackEvent } from '@modules/pendo/hooks/usePendoTrackEvent'
import { PendoCustomListMoveOrCopyEventType, PendoEventType } from '@modules/pendo/types'
import shortlistApi from '@modules/shortlist/api'
import ShortlistTable from '@modules/shortlist/components/ShortlistTable'
import AddIcon from '@mui/icons-material/Add'
import CreateIcon from '@mui/icons-material/Create'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { CircularProgress } from '@mui/material'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { useNotification } from '@shared/hooks/useNotification'
import { usePersistStore } from '@shared/hooks/usePersistStore'
import { useQueryParams } from '@shared/hooks/useQueryParams'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { truncateWithDots } from '@shared/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { UpdateManyMap } from './api/shortlist'
import AddNewShortlistSectionPopover from './components/AddNewShortlistSection/AddNewShortlistSectionPopover'
import EditCustomColumnPopover from './components/EditCustomColumnPopover'
import EditCustomColumnSectionPopover from './components/EditCustomColumnSection/EditCustomColumnSectionPopover'
import EditNotePopover from './components/EditNotePopover'
import EditShortlistsPopup from './components/EditShortlistsPopup'
import { EmptyState } from './components/EmptyState'
import ImportProspectsPopup from './components/ImportProspectsPopup'
import { ImportProspectsQsFilterKey } from './components/ImportProspectsPopup/types'
import MoveProfilesPopup from './components/MoveProfilesPopup'
import { PaginationContainer } from './components/PaginationContainer'
import { ShortlistConfirmModals } from './components/ShortlistConfirmModals'
import ShortlistControls from './components/ShortlistControls/ShortlistControls'
import ShortlistFilterPopover from './components/ShortlistFilterPopover'
import { ShortlistHeader } from './components/ShortlistHeader'
import { DEFAULT_SHORTLIST_PROFILES_PER_PAGE } from './constants'
import { useInvalidateMutation } from './hooks/useInvalidateMutation'
import { usePopover } from './hooks/usePopover'
import { useShortlistDownload } from './hooks/useShortlistDownload'
import { useShortlistPageData, useShortlists } from './hooks/useShortlistMap'
import { useSyncedShortlist } from './hooks/useSyncedShortlist'
import {
  ShortListTitleInner,
  ShortlistCounterContainer,
  ShortlistPagePaper,
  ShortlistPagePaperInner,
  ShortlistPageWrapper,
  ShortlistTableScrollbar,
  ShortlistTableScrollbarWrapper,
  ShortlistTitleContainer,
  ShortlistTitleDivider,
  TextButton,
} from './index.styles'
import {
  DEFAULT_SHORTLIST_SORT,
  ShortListCustomField,
  Shortlist,
  ShortlistCategoriesConfigProps,
  ShortlistColumnFilter,
  ShortlistFilterField,
  ShortlistItemProfile,
  ShortlistProfile,
  ShortlistSort,
} from './types'

const ShortlistPage = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const { user } = useAuthContext()
  const { isDesktop, isLgDesktop } = useScreenDimensions()
  const { trackPendoEvent } = usePendoTrackEvent()
  const { trackActivity } = useTrackUserActivity()
  const { showNotification } = useNotification()

  const { shortlistSortMap, setShortlistSort } = usePersistStore()
  const {
    hasAccessToMultipleShortlists,
    hasAccessToAlerts,
    canDownloadShortlists,
    canCopyProspects,
    canCreateCustomColumns,
    canFilterShortlist,
  } = useFeatureFlags()

  const {
    alertsShortlist,
    canAddShortlists,
    rawShortlists = [],
    shortlists,
    isUniqueName,
    isFetching,
    isInitialLoading,
  } = useShortlists()

  const { get, clearOneSilently } = useQueryParams()

  const [query, setQuery] = useParamsFromQuery(
    {
      id: NumberParam,
      page: NumberParam,
      scrollY: NumberParam,
      scrollX: NumberParam,
    },
    {
      removeDefaultsFromUrl: true,
    },
  )

  const { scrollX = 0, scrollY = 0 } = query || {}

  const events = useMemo(
    () =>
      user?.events?.filter(
        userEvent =>
          userEvent.isRequestingEnabled &&
          userEvent.hasCompanyProfile &&
          (user.p1PlusAccessConfig?.events === P1PlusAccessConfigEvent.full || userEvent.isAttending),
      ) || [],
    [user?.events, user?.p1PlusAccessConfig?.events],
  )

  const eventIdFromQs = get(ImportProspectsQsFilterKey.event)
  const isEventFromQsExists = events.some(e => e.id === Number(eventIdFromQs))

  const shortlistTableRef = useRef<Nullable<HTMLDivElement>>(null)

  const [selectedProfileIds, setSelectedProfileIds] = useState<Array<number>>([])
  const [confirmDeleteShortlistId, setConfirmDeleteShortlistId] = useState<Nullable<number>>(null)
  const [customColumnToEdit, setCustomColumnToEdit] = useState<ShortListCustomField | undefined>(undefined)

  const [isEditShortlistsPopupShown, setIsEditShortlistsPopupShown] = useState<boolean>(false)
  const [isConfirmProfileDeletePopupShown, setIsConfirmProfileDeletePopupShown] = useState<boolean>(false)
  const [isConfirmAlertsRemovePopupShown, setIsConfirmAlertsRemovePopupShown] = useState<boolean>(false)
  const [importProspectsPopupVisible, setImportProspectsPopupVisible] = useState<boolean>(false)
  const [confirmExitPopupCloseAction, setConfirmExitPopupCloseAction] = useState<Nullable<() => void>>(null)
  const [isActionButtonsDisabled, setIsActionButtonsDisabled] = useState<boolean>(false)
  const [profileNoteData, setProfileNoteData] =
    useState<Nullable<{ profileId?: number; note: string; profileType: string }>>(null)
  const [profileCustomColumnData, setProfileCustomColumnData] =
    useState<Nullable<{ profileId?: number; fieldId: number; customFieldValue: string }>>(null)
  const [showDuplicateError, setShowDuplicateError] = useState<boolean>(false)
  const [tablePage, setTablePage] = useState(1)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [shortlistFilters, setShortlistFilters] = useState<ShortlistColumnFilter[] | undefined>(undefined)

  const [selectProfilesNoticeAnchor, setSelectProfilesNoticeAnchor] =
    useState<Nullable<{ anchor: Nullable<HTMLElement>; text: string }>>(null)

  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300)

  const {
    anchorEl: addShortlistPopoverAnchor,
    handleOpen: handleAddShortlistPopoverOpen,
    handleClose: handleAddShortlistPopoverClose,
  } = usePopover<HTMLElement>()

  const { anchorEl: categoriesAnchorEl, setAnchorEl: setCategoriesAnchorEl } = usePopover<HTMLElement>()

  const {
    anchorEl: dragProfileToShortlistAnchor,
    handleOpen: handleDragProfileToShortlistOpen,
    handleClose: handleDragProfileToShortlistClose,
    setAnchorEl: setDragProfileToShortlistAnchor,
  } = usePopover<HTMLElement>()

  const {
    anchorEl: editCustomColumnPopoverAnchor,
    handleOpen: handleEditCustomColumnPopoverOpen,
    handleClose: handleEditCustomColumnPopoverClose,
  } = usePopover<HTMLElement>()

  const {
    anchorEl: moveProfilesToShortlistAnchor,
    handleOpen: handleMoveProfilesToShortlistOpen,
    handleClose: handleMoveProfilesToShortlistClose,
    setAnchorEl: setMoveProfilesToShortlistAnchor,
  } = usePopover<HTMLElement>()

  const { anchorEl: filterShortlistAnchor, handleOpen: handleFilterShortlistOpen } = usePopover<HTMLElement>()
  const {
    anchorEl: addAlertPopoverAnchor,
    handleOpen: handleAddAlertPopoverOpen,
    handleClose: handleAddAlertPopoverClose,
  } = usePopover<HTMLElement>()
  const { anchorEl: noteAnchor, handleOpen: handleNotePopoverOpen } = usePopover<HTMLElement>()
  const { anchorEl: customColumnAnchor, handleOpen: handleCustomColumnOpen } = usePopover<HTMLElement>()

  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)

  useEffect(() => {
    if (eventIdFromQs) {
      if (isEventFromQsExists) {
        setImportProspectsPopupVisible(true)
        return
      }

      clearOneSilently(ImportProspectsQsFilterKey.event)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventIdFromQs])

  const tabs = useMemo(
    () =>
      [...shortlists]
        .sort((shortlistA, shortlistB) => new Date(shortlistB.createdAt).getTime() - new Date(shortlistA.createdAt).getTime())
        .map(i => ({ id: i.id, label: i.name })),
    [shortlists],
  )

  const [activeShortlistId, setActiveShortlistId] = useSyncedShortlist({
    query,
    setQuery,
    tabs,
    isFetching,
    userId: user?.id,
    tablePage,
    setTablePage,
  })

  const shortlistSort = shortlistSortMap[activeShortlistId!] || DEFAULT_SHORTLIST_SORT

  const activeShortlist = useMemo(() => shortlists.find(i => i.id === activeShortlistId), [activeShortlistId, shortlists])

  const profiles = useMemo(() => activeShortlist?.profiles || [], [activeShortlist])

  useEffect(() => {
    setShortlistFilters(
      activeShortlist?.filters?.map(filter => ({
        ...filter,
        type: filter.attribute as ShortlistFilterField,
        attribute: (filter.nestedAttribute as number) || (filter.attribute! as ShortlistFilterField),
      })) || [],
    )
  }, [activeShortlist])

  const { profilesPageData, totalProfilesAmount, isPageDataLoading } = useShortlistPageData({
    shortlistId: activeShortlistId,
    isEmptyShortlist: !profiles.length,
    from: Math.max(tablePage - 1, 0) * DEFAULT_SHORTLIST_PROFILES_PER_PAGE,
    sort: shortlistSort,
    searchQuery: debouncedSearchQuery,
    filters: shortlistFilters || [],
  })

  useEffect(() => {
    if (shortlistTableRef.current && (scrollX || scrollY)) {
      window.scrollTo({
        top: scrollY || undefined,
        left: scrollX || undefined,
        behavior: 'smooth',
      })

      // We need to use setTimeout in this case due to Safari-only issue.
      // `window.scrollTo` works great in all other browsers, but not in Safari.
      // So that is why we are wrapping `window.scroll` into setTimeout here.
      setTimeout(() => {
        window.scroll({ top: scrollY || undefined, left: scrollX || undefined, behavior: 'smooth' })

        setQuery({ scrollY: undefined, scrollX: undefined })
      }, 2000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollX, scrollY, shortlistTableRef.current])

  useEffect(() => {
    setSelectedProfileIds([])
    setTablePage(query?.page || 1)
  }, [activeShortlistId, query?.page])

  const { mutate: deleteShortlist } = useInvalidateMutation(shortlistApi.deleteShortlist.fn, [shortlistApi.getShortlists.key])
  const { mutate: updateShortlist } = useInvalidateMutation(shortlistApi.updateShortlistById.fn, [shortlistApi.getShortlists.key])

  const { mutate: bulkShortlistUpdate } = useInvalidateMutation(shortlistApi.bulkShortlistUpdate.fn, [
    shortlistApi.getShortlists.key,
  ])

  const { mutate: createShortlistCustomColumns } = useInvalidateMutation(shortlistApi.createShortlistCustomColumn.fn, [
    shortlistApi.getShortlists.key,
    [shortlistApi.getShortlistPageData.key, activeShortlistId],
  ])

  const { mutate: editShortlistCustomColumn } = useInvalidateMutation(shortlistApi.editShortlistCustomColumn.fn, [
    shortlistApi.getShortlists.key,
  ])
  const { mutate: createProfilesAlerts } = useInvalidateMutation(
    alertsApi.createProfileAlert.fn,
    [alertsApi.fetchProfilesAlerts.key],
    {
      onSuccess: handleAddAlertPopoverClose,
    },
  )

  const { mutate: updateTableConfig } = useInvalidateMutation(
    shortlistApi.updateCategoriesConfig.fn,
    [[shortlistApi.getShortlistPageData.key, activeShortlistId]],
    {
      onSuccess: () => {
        queryClient.refetchQueries({ queryKey: [shortlistApi.getShortlists.key] })
      },
    },
  )

  const { downloadShortlist, fetchDownloadStatus } = useShortlistDownload({ activeShortlistId })

  const { mutate: moveProfileBetweenShortlists } = useMutation({
    mutationFn: shortlistApi.moveProfileBetweenShortlists.fn,
    onSuccess: (_, { fromShortlistId, toShortlistIds, profileIds, copyProspects }) => {
      onSetMoveProfilesToShortlistAnchor(null)
      handleDragProfileToShortlistClose()

      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })

      const pageAmount = totalProfilesAmount
        ? Math.ceil((totalProfilesAmount - profileIds.length) / DEFAULT_SHORTLIST_PROFILES_PER_PAGE)
        : 1
      setTablePage(Math.min(pageAmount, tablePage))

      queryClient.refetchQueries({ queryKey: [shortlistApi.getShortlistPageData.key, fromShortlistId] })

      toShortlistIds.forEach(id => {
        queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, id] })
      })

      // slight delay to let UI react first
      setTimeout(() => {
        setIsActionButtonsDisabled(false)
        setSelectedProfileIds(selectedProfileIds.filter(selectedProfileId => !profileIds.includes(selectedProfileId)))
      }, 1000)

      const SHORTLIST_ACTION_TRANSLATION_KEY = copyProspects
        ? translationKeys.PROSPECT_COPIED_TO_SHORTLIST
        : translationKeys.PROSPECT_MOVED_TO_SHORTLIST

      const prospectCountText = t(translationKeys.COUNT_PROSPECTS, { count: profileIds.length })

      const toShortlistText = t(SHORTLIST_ACTION_TRANSLATION_KEY, {
        targetShortlistName: shortlists.find(shortlist => shortlist.id === toShortlistIds[0])?.name,
        count: toShortlistIds.length,
      })

      const message = `${prospectCountText}&nbsp;${toShortlistText}`

      showNotification(parse(message), 'success')
    },
    onError: () => {
      setShowDuplicateError(true)
      setIsActionButtonsDisabled(false)
      setTimeout(() => setShowDuplicateError(false), 3000)
    },
  })

  const { mutate: updateShortlistProfiles } = useMutation({
    mutationFn: shortlistApi.updateShortlistProfiles.fn,
    onSuccess: (_, variables) => {
      const pageAmount = totalProfilesAmount
        ? Math.ceil((totalProfilesAmount - selectedProfileIds.length) / DEFAULT_SHORTLIST_PROFILES_PER_PAGE)
        : 1
      setTablePage(Math.min(pageAmount, tablePage))
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlists.key] })
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })
      const newShortlistData = profilesPageData.filter(item => !selectedProfileIds.includes(item.profileId))
      queryClient.setQueryData([shortlistApi.getShortlistPageData.key], () => newShortlistData)
      if (variables?.addToShortlistIds?.length) {
        variables.addToShortlistIds.forEach(shortlistId =>
          queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, shortlistId] }),
        )
      }

      setIsConfirmProfileDeletePopupShown(false)
      handleMoveProfilesToShortlistClose()
      setIsActionButtonsDisabled(false)
      setSelectedProfileIds([])
    },
  })

  const { mutate: rawUpdateShortlistProfileById } = useMutation({
    mutationFn: shortlistApi.updateShortlistProfileById.fn,
    onSuccess: async (_, { data }) => {
      setNoteEditAnchor(null)

      const shouldRefetchProfilesForCustomField =
        data.customFieldValues &&
        ((shortlistSort.customFieldId && shortlistSort.customFieldId in data.customFieldValues) ||
          shortlistFilters?.some(sf => sf.attribute in data.customFieldValues!))

      const shouldRefetchProfiles =
        shortlistSort.fieldName! in data ||
        shortlistFilters?.some(i => i.attribute in data) ||
        shouldRefetchProfilesForCustomField

      if (shouldRefetchProfiles) {
        queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })
      }

      queryClient.invalidateQueries({
        queryKey: [shortlistApi.getSortableFields.key, activeShortlistId],
      })
    },
  })

  const { mutate: deleteProfilesAlerts } = useMutation({
    mutationFn: alertsApi.deleteProfilesAlerts.fn,
    onSuccess: (_, variables) => {
      variables.forEach(deletedProfile => {
        queryClient.invalidateQueries({ queryKey: [alertsApi.fetchProfilesAlerts.key, deletedProfile.id] })
      })

      const pageAmount = totalProfilesAmount
        ? Math.ceil((totalProfilesAmount - selectedProfileIds.length) / DEFAULT_SHORTLIST_PROFILES_PER_PAGE)
        : 1
      setTablePage(Math.min(pageAmount, tablePage))
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })

      setSelectedProfileIds([])
      setIsConfirmAlertsRemovePopupShown(false)
    },
  })

  const handleShortListsUpdate = useCallback(
    (data: UpdateManyMap) => {
      bulkShortlistUpdate(data)
      setIsEditShortlistsPopupShown(false)
    },
    [bulkShortlistUpdate, setIsEditShortlistsPopupShown],
  )

  const updateShortlistProfileById = useCallback(
    (updateData: { id: ShortlistProfile['id']; data: Partial<ShortlistProfile> }) => {
      // optimistically update cached data
      const matchingQueries = queryClient.getQueriesData<{ result: ShortlistProfile[] }>({
        queryKey: [shortlistApi.getShortlistPageData.key],
        exact: false,
      })

      const updatedCustomFieldValues = (profile: ShortlistProfile) => {
        return { ...profile.customFieldValues, ...updateData.data.customFieldValues }
      }

      matchingQueries.forEach(([queryKey, existingProfilesData]) => {
        if (!existingProfilesData?.result?.length) {
          return
        }

        const updatedProfiles = existingProfilesData.result.map(profile =>
          profile.id === updateData.id
            ? { ...profile, ...updateData.data, customFieldValues: updatedCustomFieldValues(profile) }
            : profile,
        )

        queryClient.setQueryData(queryKey, { ...existingProfilesData, result: updatedProfiles })
      })

      // performing real update
      rawUpdateShortlistProfileById(updateData)

      const shouldRefetchProfiles =
        updateData.data.customFieldValues &&
        shortlistSort.customFieldId &&
        shortlistSort.customFieldId === profileCustomColumnData?.fieldId
      if (shouldRefetchProfiles) {
        queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key] })
      }
    },
    [queryClient, profileCustomColumnData?.fieldId, shortlistSort.customFieldId, rawUpdateShortlistProfileById],
  )

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (tablePage !== 1) {
      setTablePage(1)
    }
  }

  const onEventSelectionPopoverClick = useCallback((event: Nullable<React.MouseEvent<HTMLElement>>) => {
    if (event?.currentTarget) {
      setImportProspectsPopupVisible(true)
    } else {
      setImportProspectsPopupVisible(false)
    }
  }, [])

  const onDeleteProfiles = () => {
    if (!activeShortlistId) {
      return
    }

    const profilesToDelete = selectedProfileIds
      .map(id => profiles.find(i => i.id === id))
      .filter(Boolean) as ShortlistItemProfile[]

    trackPendoEvent(
      PendoEventType.ShortlistProfileDelete,
      preparePendoCustomListEvent({
        eventType: 'Click on “Confirm” button',
        user,
      }),
    )

    updateShortlistProfiles({
      profiles: profilesToDelete,
      removeFromShortlistIds: [activeShortlistId],
    })
  }

  const onRemoveAlerts = () => {
    if (!activeShortlistId) {
      return
    }

    const profileAlertsToDelete = selectedProfileIds.map(id => {
      const profileToDelete = profiles.find(i => i.id === id)
      return { id: profileToDelete?.searchDocId, '@type': profileToDelete?.profileType }
    })

    deleteProfilesAlerts(profileAlertsToDelete)
  }

  const onFilterShortlistButtonClick = useCallback(
    (event: Nullable<React.MouseEvent<HTMLElement>>) => {
      if (searchQuery?.length) return
      handleFilterShortlistOpen(event)
    },
    [searchQuery.length, handleFilterShortlistOpen],
  )

  const onEditCustomColumnButtonClick = (event: Nullable<React.MouseEvent<HTMLElement>>, id: number) => {
    handleEditCustomColumnPopoverOpen(event)
    if (event?.currentTarget && id) {
      setCustomColumnToEdit(activeShortlist?.customFields?.find(customColumn => customColumn.id === id))
    }
  }

  const onSetMoveProfilesToShortlistAnchor = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    handleMoveProfilesToShortlistOpen(selectedProfileIds?.length ? event : null)
    if (event?.currentTarget && !selectedProfileIds?.length) {
      sendMoveOrCopyEvent({
        eventType: PendoCustomListMoveOrCopyEventType.NoProfilesClick,
      })

      setSelectProfilesNoticeAnchor({ anchor: event.currentTarget, text: translationKeys.YOU_NEED_TO_SELECT_TO_MOVE })
    }
  }

  const onSetAddAlertsToProfilesAnchor = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    handleAddAlertPopoverOpen(selectedProfileIds?.length ? event : null)
    if (event?.currentTarget && !selectedProfileIds?.length) {
      setSelectProfilesNoticeAnchor({
        anchor: event.currentTarget,
        text: translationKeys.YOU_NEED_TO_SELECT_PROFILES_TO_ADD_ALERTS,
      })
    }
  }

  const handleCloseAddCustomColumnPopover = () => {
    handleEditCustomColumnPopoverClose()
    setCustomColumnToEdit(undefined)
  }

  const profilePageIds = useMemo(() => profilesPageData?.map(profile => profile.profileId), [profilesPageData])

  const handleSelectProfile = useCallback(
    (profileIds: number | number[]) => {
      if (isArray(profileIds)) {
        if (!profileIds.length) {
          // unselecting only the page where we are
          setSelectedProfileIds(existingIds => existingIds.filter(id => !profilePageIds.includes(id)))
        }

        setSelectedProfileIds(existingIds => [...Array.from(new Set([...profileIds, ...existingIds]))])
        return
      }

      setSelectedProfileIds(prevState => {
        if (prevState.includes(profileIds)) {
          return prevState.filter(i => i !== profileIds)
        }
        return [...prevState, profileIds]
      })
    },
    [setSelectedProfileIds, profilePageIds],
  )

  const handleClickProfile = useCallback(() => {
    if (shortlistTableRef.current?.scrollLeft) {
      setQuery({ scrollX: shortlistTableRef.current?.scrollLeft }, 'pushIn')
    }

    if (window.scrollY) {
      setQuery({ scrollY: window.scrollY }, 'pushIn')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setQuery, shortlistTableRef.current])

  const handleAddNewSuccess = (sh?: Shortlist) => {
    if (sh) {
      handleAddShortlistPopoverClose()
      setActiveShortlistId(sh.id)
      navigate(`/plus/custom-list?id=${sh.id}&page=${tablePage}`)
    }
  }

  const handleCustomColumnEditConfirm = useCallback(
    (customField: ShortListCustomField, closePopup: boolean) => {
      if (closePopup) {
        handleEditCustomColumnPopoverClose()
        setCustomColumnToEdit(undefined)
      } else {
        setCustomColumnToEdit(customField)
      }

      if (customField.id) {
        editShortlistCustomColumn({
          customField,
          shortlistId: activeShortlistId as number,
        })
      } else {
        createShortlistCustomColumns({
          customField,
          shortlistId: activeShortlistId as number,
        })
      }

      const pendoTrackingEvent = preparePendoCustomColumnEvent({
        anchorEl: categoriesAnchorEl,
        customField,
        user,
        eventType: 'edit',
      })

      trackPendoEvent(customField.id ? PendoEventType.CustomColumnEdited : PendoEventType.CustomColumnCreated, pendoTrackingEvent)
    },
    [
      categoriesAnchorEl,
      user,
      trackPendoEvent,
      handleEditCustomColumnPopoverClose,
      editShortlistCustomColumn,
      activeShortlistId,
      createShortlistCustomColumns,
    ],
  )

  const setNoteEditAnchor = (
    event: Nullable<React.MouseEvent<HTMLElement>>,
    data?: { profileId: number; note: string; profileType: string },
  ) => {
    event?.stopPropagation()

    setProfileNoteData(data?.profileId ? data : null)

    handleNotePopoverOpen(event)
  }

  const setCustomColumnEditAnchor = useCallback(
    (event: Nullable<React.MouseEvent<HTMLElement>>, data?: { profileId: number; fieldId: number; customFieldValue: string }) => {
      event?.stopPropagation()

      setProfileCustomColumnData(data?.profileId ? data : null)

      handleCustomColumnOpen(event)
    },
    [handleCustomColumnOpen, setProfileCustomColumnData],
  )

  const onSetDragProfileAnchor = (event: Nullable<React.MouseEvent<HTMLElement>>) => {
    event?.stopPropagation()
    handleDragProfileToShortlistOpen(event)
  }

  const deleteNoteHandler = (deleteNodeCallback?: () => void) => {
    if (!profileNoteData?.note || !profileNoteData?.profileId) {
      return
    }

    updateShortlistProfileById({ id: profileNoteData?.profileId, data: { note: '' } })
    deleteNodeCallback?.()
  }

  const deleteCustomColumnHandler = useCallback(
    (deleteNodeCallback?: () => void) => {
      if (
        !profileCustomColumnData?.customFieldValue ||
        !profileCustomColumnData?.profileId ||
        !profileCustomColumnData?.fieldId
      ) {
        return
      }

      updateShortlistProfileById({
        id: profileCustomColumnData?.profileId,
        data: { customFieldValues: { [profileCustomColumnData?.fieldId]: profileCustomColumnData?.customFieldValue } },
      })
      deleteNodeCallback?.()
    },
    [
      updateShortlistProfileById,
      profileCustomColumnData?.customFieldValue,
      profileCustomColumnData?.profileId,
      profileCustomColumnData?.fieldId,
    ],
  )

  const makeFieldUpdater = <K extends keyof ShortlistProfile>(
    fieldName: K,
    trackKey?: string,
    getTrackPayload?: (value: ShortlistProfile[K], profileType?: string) => Record<string, unknown>,
  ) => {
    return (profileId: number, value: ShortlistProfile[K], profileType?: string) => {
      updateShortlistProfileById({ id: profileId, data: { [fieldName]: value } } as unknown as {
        id: ShortlistProfile['id']
        data: Partial<ShortlistProfile>
      })
      if (trackKey && getTrackPayload) {
        trackActivity(trackKey, getTrackPayload(value, profileType))
      }
    }
  }

  const updateNdaFieldHandler = makeFieldUpdater('nda')

  const updatePriorityFieldHandler = makeFieldUpdater(
    'priority',
    EVENT_ACTIVITY_KEYS.CUSTOM_LIST.PRIORITY_COLUMN.EDIT,
    (priority, profileType) => ({
      custom_list_id: activeShortlistId,
      option_selected: priority,
      profile_type: profileType,
    }),
  )
  const updateTagIdFieldHandler = makeFieldUpdater(
    'tagId',
    EVENT_ACTIVITY_KEYS.CUSTOM_LIST.STATUS.SELECT,
    (tagId, profileType) => ({ selectedOption: tagId, profileType }),
  )

  const handleProfileDrop = useCallback(
    (fromShortlistId: number, toShortlistId: number, droppedItem: unknown, dropTarget: Nullable<HTMLElement>) => {
      setDragProfileToShortlistAnchor(dropTarget)

      setIsActionButtonsDisabled(true)
      const { profileId } = droppedItem as { profileId: number }

      moveProfileBetweenShortlists({
        fromShortlistId,
        toShortlistIds: [toShortlistId],
        profileIds: [profileId],
        throwOnDuplicates: true,
      })

      sendMoveOrCopyEvent({
        eventType: PendoCustomListMoveOrCopyEventType.MoveHandle,
        profileTypes: [profiles.find(p => p.id === profileId)?.profileType ?? ''],
        toShortlistIds: [toShortlistId],
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profiles],
  )

  const handleSaveAlertFrequency = useCallback(
    (frequency?: AlertFrequencyType) => {
      const profileAlerts: ProfileAlert[] = selectedProfileIds
        .map(id => {
          const profile = profiles.find(i => i.id === id)
          return (profile ? { id: profile.searchDocId, type: profile.profileType } : undefined) as ProfileAlert
        })
        .filter(profileAlert => !!profileAlert)
      const alertFrequency = frequency === AlertFrequencyType.NO_NOTIFICATION ? undefined : frequency

      if (profileAlerts?.length) {
        trackActivity(EVENT_ACTIVITY_KEYS.ALERTS.EMAIL.ADD_BULK, {
          custom_list_id: activeShortlistId,
          action: 'clicked save button',
          alerts: profileAlerts.map(i => ({
            profile_type: i.type,
            new_alert_frequency: frequency || AlertFrequencyType.NO_NOTIFICATION,
            previous_alert_frequency: i.alertFrequency,
          })),
        })
      }

      createProfilesAlerts({ profileAlerts, alertFrequency })
      handleAddAlertPopoverClose()
    },
    [createProfilesAlerts, selectedProfileIds, profiles, activeShortlistId, handleAddAlertPopoverClose, trackActivity],
  )

  const handleSaveCancel = useCallback(() => {
    trackActivity(EVENT_ACTIVITY_KEYS.ALERTS.EMAIL.ADD_BULK, {
      custom_list_id: activeShortlistId,
      action: 'clicked cancel button',
    })

    handleAddAlertPopoverClose()
  }, [activeShortlistId, handleAddAlertPopoverClose, trackActivity])

  const handleSelectAlertFrequency = useCallback(
    (frequency?: AlertFrequencyType) => {
      const profileAlerts: ProfileAlert[] = selectedProfileIds
        .map(id => {
          const profile = profiles.find(i => i.id === id)
          return (profile ? { id: profile.searchDocId, type: profile.profileType } : undefined) as ProfileAlert
        })
        .filter(profileAlert => !!profileAlert)

      if (profileAlerts?.length) {
        trackActivity(EVENT_ACTIVITY_KEYS.ALERTS.EMAIL.ADD_BULK, {
          custom_list_id: activeShortlistId,
          action: 'frequency selected',
          alerts: profileAlerts.map(i => ({
            profile_type: i.type,
            new_alert_frequency: frequency || AlertFrequencyType.NO_NOTIFICATION,
            previous_alert_frequency: i.alertFrequency,
          })),
        })
      }
    },
    [trackActivity, profiles, selectedProfileIds, activeShortlistId],
  )

  const onShortlistPageChange = useCallback(
    (_: React.ChangeEvent<unknown> | null, page: number, preventUrlUpdate?: boolean) => {
      setTablePage(page)
      if (!preventUrlUpdate) {
        navigate(`/plus/custom-list?id=${query.id}&page=${page}`)
      }
      if (window.scrollY > 250) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    [navigate, query.id],
  )

  const sendMoveOrCopyEvent = useCallback(
    (data: { toShortlistIds?: number[]; eventType: PendoCustomListMoveOrCopyEventType; profileTypes?: string[] }) => {
      const { toShortlistIds, eventType, profileTypes } = data
      const pendoTrackingEvent = prepareMoveOrCopyEventData({
        location: location.pathname,
        user,
        eventType,
        destinationCustomListIds: toShortlistIds,
        originalCustomListId: activeShortlistId,
        profileTypes:
          profileTypes ?? selectedProfileIds.map(id => profiles.find(profile => profile.id === id)?.profileType ?? ''),
      })

      trackPendoEvent(PendoEventType.CustomListMoveOrCopy, pendoTrackingEvent)
    },
    [selectedProfileIds, activeShortlistId, location, trackPendoEvent, profiles, user],
  )

  const handleConfirmMoveProfilesClick = (toShortlistIds: Shortlist['id'][], copyProspects?: boolean) => {
    if (!activeShortlistId) {
      return
    }

    setIsActionButtonsDisabled(true)

    moveProfileBetweenShortlists({
      toShortlistIds,
      profileIds: selectedProfileIds,
      fromShortlistId: activeShortlistId,
      copyProspects,
    })

    sendMoveOrCopyEvent({
      toShortlistIds,
      eventType: copyProspects ? PendoCustomListMoveOrCopyEventType.Copy : PendoCustomListMoveOrCopyEventType.Move,
    })
  }
  const handleSortingChange = useCallback(
    (sort: ShortlistSort) => {
      if (activeShortlistId) {
        setShortlistSort(activeShortlistId, sort)
        onShortlistPageChange(null, 1, true)
      }
    },
    [activeShortlistId, onShortlistPageChange, setShortlistSort],
  )

  const handleEditShortlistClick = useCallback(() => {
    setIsEditShortlistsPopupShown(true)
  }, [setIsEditShortlistsPopupShown])

  const handleShortlistFilterChange = useCallback(
    (newFilters: ShortlistColumnFilter[]) => {
      if (newFilters === shortlistFilters) return

      setShortlistFilters(newFilters)
      onShortlistPageChange(null, 1, true)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeShortlistId, shortlistFilters, onShortlistPageChange],
  )

  const handleTabChange = useCallback(
    (id: number) => {
      setActiveShortlistId(id)
      setTablePage(1)
      navigate(`/plus/custom-list?id=${id}`)
    },
    [navigate, setTablePage, setActiveShortlistId],
  )

  const updateConfig = useCallback(
    (config: ShortlistCategoriesConfigProps) => {
      if (activeShortlistId) {
        updateTableConfig({ id: activeShortlistId, config })
      }
    },
    [activeShortlistId, updateTableConfig],
  )

  const closeFiltersPopup = useCallback(() => {
    onFilterShortlistButtonClick(null)
    updateShortlist({
      shortlistId: activeShortlistId!,
      params: {
        name: activeShortlist!.name,
        filters: shortlistFilters?.map(filter => ({
          ...filter,
          attribute: filter.type,
          combinator: filter.combinator || undefined,
        })),
      },
    })
  }, [activeShortlist, activeShortlistId, onFilterShortlistButtonClick, shortlistFilters, updateShortlist])

  const handleDeleteProfilesClick = useCallback(
    (e: Nullable<React.MouseEvent<HTMLElement>>) => {
      if (selectedProfileIds.length) {
        setIsConfirmProfileDeletePopupShown(true)
      } else {
        setSelectProfilesNoticeAnchor({
          anchor: e!.currentTarget,
          text: translationKeys.YOU_NEED_TO_SELECT_ALERTS_TO_DELETE,
        })
      }
    },
    [selectedProfileIds, setIsConfirmProfileDeletePopupShown, setSelectProfilesNoticeAnchor],
  )

  const handleDownloadClick = useCallback(
    () => downloadShortlist({ id: activeShortlistId, date: DateTime.now().toFormat('yyyy-MM-dd_HH_mm') }),
    [activeShortlistId, downloadShortlist],
  )

  const handleCloseNotePopover = useCallback(() => {
    trackPendoEvent(
      PendoEventType.ShortlistProfileDelete,
      preparePendoCustomListEvent({
        eventType: 'Click on x',
        user,
      }),
    )
    setSelectProfilesNoticeAnchor(null)
  }, [trackPendoEvent, user])

  const handleDeleteShortlistConfirm = useCallback(() => {
    if (confirmDeleteShortlistId) {
      deleteShortlist({ id: confirmDeleteShortlistId })
      setConfirmDeleteShortlistId(null)

      if (query?.id === confirmDeleteShortlistId) {
        setQuery({ page: 1 })
      }

      if (rawShortlists.length === 1) {
        setIsEditShortlistsPopupShown(false)
      }
    }
  }, [confirmDeleteShortlistId, deleteShortlist, query?.id, rawShortlists.length, setQuery])

  const handleCancelDeleteShortlist = useCallback(() => {
    setIsEditShortlistsPopupShown(true)
    setConfirmDeleteShortlistId(null)
  }, [])

  const handleCancelRemoveAlerts = useCallback(() => {
    trackPendoEvent(
      PendoEventType.ShortlistProfileDelete,
      preparePendoCustomListEvent({
        eventType: 'Click on “Cancel” button',
        user,
      }),
    )

    setIsConfirmProfileDeletePopupShown(false)
  }, [trackPendoEvent, user])

  const totalPages = useMemo(() => {
    return totalProfilesAmount ? Math.ceil(totalProfilesAmount / DEFAULT_SHORTLIST_PROFILES_PER_PAGE) : 0
  }, [totalProfilesAmount])

  const shouldShowPagination = useMemo(() => {
    return totalPages > 1 && !!profiles.length
  }, [profiles, totalPages])

  const moveProspectsLabelKey = useMemo(
    () => (canCopyProspects ? translationKeys.MOVE_OR_COPY_TO_CUSTOM_LIST : translationKeys.MOVE_TO_DIFFERENT_CUSTOM_LIST),
    [canCopyProspects],
  )

  const removeAlertsTexts = useMemo(() => {
    if (selectedProfileIds?.length > 1) {
      return {
        title: t(translationKeys.DELETE_ALERTS_FROM_LIST_TITLE),
        text: t(translationKeys.DELETE_ALERTS_FROM_LIST_SUBTITLE),
      }
    }
    return { title: t(translationKeys.DELETE_ALERT_FROM_LIST_TITLE), text: t(translationKeys.DELETE_ALERT_FROM_LIST_SUBTITLE) }
  }, [t, selectedProfileIds?.length])

  const counterTextData = useMemo(() => {
    if (!profiles?.length) {
      return {
        text: '',
        showEditFilterBtn: false,
      }
    }

    const filteredCount = totalProfilesAmount
    const totalCount = profiles?.length ?? 0

    if (searchQuery) {
      return {
        text: `${filteredCount} of ${totalCount} prospects filtered containing '${searchQuery}'`,
        showEditFilterBtn: false,
      }
    }

    if (shortlistFilters?.length) {
      return {
        text: `${filteredCount} of ${totalCount} prospects`,
        showEditFilterBtn: true,
      }
    }

    return {
      text: `${totalCount} prospects`,
      showEditFilterBtn: false,
    }
  }, [totalProfilesAmount, searchQuery, profiles?.length, shortlistFilters?.length])

  const manageShortlistsMenuItems = useMemo<EllipsisMenuItem[]>(() => {
    if (isDesktop) {
      return []
    }

    return [
      {
        onClick: handleAddShortlistPopoverOpen,
        label: t(translationKeys.ADD_YOUR_CUSTOM_LIST),
        icon: <AddIcon />,
        skip: !canAddShortlists,
      },
      {
        onClick: () => setIsEditShortlistsPopupShown(true),
        label: t(translationKeys.EDIT_CUSTOM_LISTS),
        icon: <CreateIcon />,
        skip: rawShortlists?.length <= 0,
      },
      {
        onClick: onEventSelectionPopoverClick,
        label: t(translationKeys.IMPORT_PROSPECTS_FROM),
        icon: <AddIcon />,
        skip: events.length <= 0,
      },
    ].filter(i => !i.skip)
  }, [
    t,
    isDesktop,
    canAddShortlists,
    rawShortlists,
    events,
    handleAddShortlistPopoverOpen,
    setIsEditShortlistsPopupShown,
    onEventSelectionPopoverClick,
  ])

  const manageProfilesMenuItems = useMemo<EllipsisMenuItem[]>(() => {
    if (isDesktop) {
      return []
    }

    return [
      {
        onClick: onSetMoveProfilesToShortlistAnchor,
        label: t(moveProspectsLabelKey),
        icon: <DriveFileMoveOutlinedIcon />,
        disabled: isActionButtonsDisabled,
        skip: !(rawShortlists?.length > 1 && hasAccessToMultipleShortlists),
      },
      {
        onClick: handleDeleteProfilesClick,
        label: t(translationKeys.DELETE_CUSTOM_LIST),
        icon: <DeleteOutlineOutlinedIcon />,
        disabled: isActionButtonsDisabled,
      },
      {
        onClick: onSetAddAlertsToProfilesAnchor,
        label: t(translationKeys.ADD_ALERTS_BUTTON),
        icon: <NotificationsNoneOutlinedIcon />,
        skip: !hasAccessToAlerts,
      },
    ].filter(item => !item.skip)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDesktop,
    rawShortlists?.length,
    hasAccessToMultipleShortlists,
    moveProspectsLabelKey,
    t,
    isActionButtonsDisabled,
    selectedProfileIds.length,
    setIsConfirmProfileDeletePopupShown,
    setSelectProfilesNoticeAnchor,
    downloadShortlist,
    activeShortlistId,
    hasAccessToAlerts,
  ])

  return (
    <ShortlistPageWrapper>
      {!isInitialLoading && (
        <>
          <ShortlistHeader
            activeTab={activeShortlistId || tabs[0]?.id || false}
            tabs={tabs}
            manageMenuItems={manageShortlistsMenuItems}
            canAddShortlists={canAddShortlists}
            eventsLoaded={!!events?.length}
            shortlistsLoaded={!!rawShortlists?.length}
            onTabChange={handleTabChange}
            onAddShortlistButtonClick={handleAddShortlistPopoverOpen}
            onEditShortlistsClick={handleEditShortlistClick}
            onProfileDrop={handleProfileDrop}
            onImportProspectsClick={onEventSelectionPopoverClick}
          />
          <PaginationContainer
            count={totalPages}
            page={tablePage}
            onChange={onShortlistPageChange}
            shouldShow={shouldShowPagination}
          >
            <ShortlistPagePaper>
              <ShortlistPagePaperInner>
                {!!profiles?.length && (
                  <ShortlistTitleContainer>
                    <Typography variant="h2">{truncateWithDots(activeShortlist?.name ?? '', 120)}</Typography>
                  </ShortlistTitleContainer>
                )}

                {!!rawShortlists?.length && !!profiles.length && (
                  <ShortlistTitleContainer>
                    {manageProfilesMenuItems?.length > 0 ? (
                      <EllipsisMenu
                        iconButtonProps={{ color: 'primary' }}
                        menuProps={{ PaperProps: { sx: { minWidth: 180 } } }}
                        items={manageProfilesMenuItems}
                      />
                    ) : (
                      <ShortListTitleInner>
                        {rawShortlists?.length > 1 && hasAccessToMultipleShortlists && (
                          <>
                            <TextButton
                              variant="text"
                              color="primary"
                              endIcon={<DriveFileMoveOutlinedIcon color="inherit" fontSize="small" sx={{ marginLeft: '-2px' }} />}
                              onClick={onSetMoveProfilesToShortlistAnchor}
                              id="shortlist-move_button"
                              disabled={isActionButtonsDisabled}
                            >
                              {t(moveProspectsLabelKey)}
                            </TextButton>
                            <ShortlistTitleDivider />
                          </>
                        )}
                        <TextButton
                          variant="text"
                          color="primary"
                          id="shortlist-delete_button"
                          endIcon={<DeleteOutlineOutlinedIcon color="inherit" fontSize="small" sx={{ marginLeft: '-6px' }} />}
                          onClick={handleDeleteProfilesClick}
                          disabled={isActionButtonsDisabled}
                        >
                          {t(translationKeys.DELETE_CUSTOM_LIST)}
                        </TextButton>
                        {canDownloadShortlists && (
                          <>
                            <ShortlistTitleDivider />
                            <TextButton
                              variant="text"
                              color="primary"
                              id="download-shortlist"
                              endIcon={
                                fetchDownloadStatus ? (
                                  <CircularProgress size="14px" />
                                ) : (
                                  <DownloadIcon color="inherit" fontSize="small" sx={{ marginLeft: '-2px' }} />
                                )
                              }
                              onClick={handleDownloadClick}
                              disabled={isActionButtonsDisabled}
                            >
                              {t(translationKeys.DOWNLOAD_CUSTOM_LIST)}
                            </TextButton>
                            <ShortlistTitleDivider />
                          </>
                        )}
                        {hasAccessToAlerts && (
                          <TextButton
                            variant="text"
                            color="primary"
                            endIcon={
                              <NotificationsNoneOutlinedIcon color="inherit" fontSize="small" sx={{ marginLeft: '-6px' }} />
                            }
                            onClick={onSetAddAlertsToProfilesAnchor}
                            id="alert_button-shortlist_screen"
                          >
                            {t(translationKeys.ADD_ALERTS_BUTTON)}
                          </TextButton>
                        )}
                      </ShortListTitleInner>
                    )}
                    <ShortlistControls
                      activeShortlist={activeShortlist}
                      searchQuery={searchQuery}
                      anchorEl={categoriesAnchorEl}
                      onSearchChange={handleSearchChange}
                      filtersCount={shortlistFilters?.length ?? 0}
                      onFilterClick={onFilterShortlistButtonClick}
                      canFilter={canFilterShortlist}
                      canCreateColumns={canCreateCustomColumns}
                      onAddColumnClick={handleEditCustomColumnPopoverOpen}
                      optionalColumnsConfig={!!user?.p1PlusAccessConfig?.optionalShortlistColumns}
                      updateTableConfig={updateConfig}
                      setAnchorEl={setCategoriesAnchorEl}
                    />
                  </ShortlistTitleContainer>
                )}
                <ShortlistCounterContainer>
                  <Typography variant="body1">{counterTextData.text}</Typography>

                  {(!!filterShortlistAnchor || counterTextData.showEditFilterBtn) && (
                    <Button variant="text" sx={{ padding: '2px', minHeight: '10px' }} onClick={onFilterShortlistButtonClick}>
                      Edit filter(s)
                    </Button>
                  )}
                </ShortlistCounterContainer>
                {!!profiles.length && (
                  <ShortlistTable
                    key={activeShortlistId}
                    activeShortlistId={activeShortlistId as number}
                    profilesPageData={profilesPageData}
                    totalProfilesAmount={totalProfilesAmount}
                    isPageDataLoading={isPageDataLoading}
                    optionalColumnsConfig={activeShortlist?.columns}
                    selectedRowIds={selectedProfileIds}
                    onSelectProfile={handleSelectProfile}
                    onClickProfile={handleClickProfile}
                    onEditNote={setNoteEditAnchor}
                    onEditCustomColumn={setCustomColumnEditAnchor}
                    onChangeNda={updateNdaFieldHandler}
                    onChangePriority={updatePriorityFieldHandler}
                    onTagUpdate={updateTagIdFieldHandler}
                    sort={shortlistSort}
                    onSortChange={handleSortingChange}
                    alertsShortlistId={alertsShortlist?.id}
                    ref={shortlistTableRef}
                    activeShortlist={activeShortlist}
                    handleEditCustomColumnSuccess={handleCustomColumnEditConfirm}
                    handleCloseEditCustomColumnPopover={handleEditCustomColumnPopoverClose}
                    onEditCustomColumnButtonClick={onEditCustomColumnButtonClick}
                    updateConfig={updateConfig}
                  />
                )}
                {!profiles?.length && <EmptyState isEcommerce={isEcommerce} />}
              </ShortlistPagePaperInner>
            </ShortlistPagePaper>
          </PaginationContainer>
        </>
      )}

      <ShortlistConfirmModals
        deleteShortlist={{
          isVisible: !!confirmDeleteShortlistId,
          onCancel: handleCancelDeleteShortlist,
          onConfirm: handleDeleteShortlistConfirm,
        }}
        removeAlertProfiles={{
          isVisible: !!isConfirmAlertsRemovePopupShown,
          onCancel: () => {
            setIsConfirmAlertsRemovePopupShown(false)
          },
          onConfirm: onRemoveAlerts,
          text: removeAlertsTexts.text,
          title: removeAlertsTexts.title,
        }}
        removeProfiles={{
          isVisible: !!isConfirmProfileDeletePopupShown,
          onCancel: handleCancelRemoveAlerts,
          onConfirm: onDeleteProfiles,
        }}
        exitShortlist={{
          isVisible: !!confirmExitPopupCloseAction,
          onCancel: () => setConfirmExitPopupCloseAction(null),
          onConfirm: () => {
            confirmExitPopupCloseAction?.()
            setConfirmExitPopupCloseAction(null)
          },
        }}
      />
      {isEditShortlistsPopupShown && (
        <EditShortlistsPopup
          isOpen
          onCancel={() => setIsEditShortlistsPopupShown(false)}
          onConfirm={handleShortListsUpdate}
          onDelete={setConfirmDeleteShortlistId}
          shouldClose={(value: Record<string, unknown>) => {
            const valueIsEmpty = !value || JSON.stringify(value) === '{}'
            if (valueIsEmpty) {
              setConfirmExitPopupCloseAction(() => setIsEditShortlistsPopupShown(false))
            }

            return valueIsEmpty
          }}
          shortlists={rawShortlists}
        />
      )}
      <NoticePopover
        id={selectProfilesNoticeAnchor ? 'select_profiles_notice_popover' : undefined}
        anchor={selectProfilesNoticeAnchor?.anchor || null}
        onClose={handleCloseNotePopover}
        title={t(translationKeys.NO_ITEM_SELECTED)}
        text={t(selectProfilesNoticeAnchor?.text as string)}
      />
      <AddNewShortlistSectionPopover
        anchor={addShortlistPopoverAnchor}
        isUniqueName={isUniqueName}
        shortlists={shortlists}
        onCancel={handleAddShortlistPopoverClose}
        onSuccess={handleAddNewSuccess}
      />
      {editCustomColumnPopoverAnchor && (
        <EditCustomColumnSectionPopover
          anchor={editCustomColumnPopoverAnchor}
          onCancel={handleCloseAddCustomColumnPopover}
          activeShortlist={activeShortlist}
          onSave={handleCustomColumnEditConfirm}
          customColumn={customColumnToEdit}
        />
      )}
      {filterShortlistAnchor && (
        <ShortlistFilterPopover
          anchor={filterShortlistAnchor}
          handleClose={closeFiltersPopup}
          activeShortlist={activeShortlist}
          existingFilters={shortlistFilters}
          filtersUpdated={handleShortlistFilterChange}
        />
      )}

      {addAlertPopoverAnchor && (
        <ProfileAlertPopover
          anchor={addAlertPopoverAnchor}
          onSave={handleSaveAlertFrequency}
          onCancel={handleSaveCancel}
          onSelect={handleSelectAlertFrequency}
          isEdit={false}
        />
      )}
      {moveProfilesToShortlistAnchor && (
        <MoveProfilesPopup
          shortlists={rawShortlists.filter(i => i.id !== activeShortlistId)}
          anchor={moveProfilesToShortlistAnchor}
          setAnchor={setMoveProfilesToShortlistAnchor}
          onConfirmClick={handleConfirmMoveProfilesClick}
          canCopy={canCopyProspects}
          shouldClose={(changes: unknown) => {
            sendMoveOrCopyEvent({
              eventType: PendoCustomListMoveOrCopyEventType.Cancel,
            })
            const areChangesEmpty = !Object.keys(changes as object).length
            if (areChangesEmpty) {
              setConfirmExitPopupCloseAction(handleMoveProfilesToShortlistClose)
            }

            return areChangesEmpty
          }}
        />
      )}
      {noteAnchor && (
        <EditNotePopover
          setAnchor={setNoteEditAnchor}
          anchor={noteAnchor}
          profileNoteData={profileNoteData}
          deleteNoteHandler={deleteNoteHandler}
          setConfirmExitPopupCloseAction={setConfirmExitPopupCloseAction}
          updateNote={updateShortlistProfileById}
          shortlistId={activeShortlistId}
        />
      )}
      {customColumnAnchor && (
        <EditCustomColumnPopover
          setAnchor={setCustomColumnEditAnchor}
          anchor={customColumnAnchor}
          profileCustomColumnData={profileCustomColumnData}
          deleteCustomColumnHandler={deleteCustomColumnHandler}
          setConfirmExitPopupCloseAction={setConfirmExitPopupCloseAction}
          updateCustomColumn={updateShortlistProfileById}
        />
      )}
      {importProspectsPopupVisible && (
        <ImportProspectsPopup
          isVisible={importProspectsPopupVisible}
          setIsVisible={setImportProspectsPopupVisible}
          events={events}
          enablePreselect={isEventFromQsExists}
          selectShortlist={id => {
            setActiveShortlistId(id)
            setTablePage(1)
            navigate(`/plus/custom-list?id=${id}`)
          }}
        />
      )}

      {showDuplicateError && (
        <ErrorPopup
          title={t(translationKeys.DUPLICATE_ERROR_TITLE)}
          text={t(translationKeys.DUPLICATE_ERROR_TEXT)}
          onClose={() => setShowDuplicateError(false)}
          width={320}
          anchor={dragProfileToShortlistAnchor}
          setAnchor={onSetDragProfileAnchor as ErrorPopupProps['setAnchor']}
        />
      )}

      {isLgDesktop && !isPageDataLoading && shortlistTableRef.current && (
        <ShortlistTableScrollbarWrapper>
          <ShortlistTableScrollbar>
            <CustomScrollbar contentRef={shortlistTableRef} key={activeShortlistId} />
          </ShortlistTableScrollbar>
        </ShortlistTableScrollbarWrapper>
      )}
    </ShortlistPageWrapper>
  )
}
export default ShortlistPage
